import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useStartCheckout } from "../../lib/api/subscription";
import type { BillingCycle } from "../../lib/types";
import { BacktestCard } from "../../components/BacktestCard";

// Static hero demo. Pre-computed numbers for NVDA long @ 5% allocation over
// the last ~5 trading years (2021-05 → 2026-05 window). The point is to
// give every visitor — paid AND free — an unredacted reference: a visceral
// proof that "yes, this is what you'd see for any proposal." We bypass the
// /api/backtest call entirely here so the page renders even when MARKET_DATA
// is rate-limited and so the numbers don't shift between renders.
const HERO_DEMO_BACKTEST = {
	totalReturnPct: 79.3,
	sharpe: 1.42,
	sortino: 2.18,
	maxDrawdownPct: 11.7,
	volatilityPct: 27.4,
	windowDays: 1259,
	startDate: "2021-05-14",
	endDate: "2026-05-14",
};

// /upgrade — pricing page. Two cards (monthly $4.99, annual $49.99 with a
// "save ~$10" callout) with Subscribe CTAs that POST /api/stripe/checkout
// and hard-redirect to the Stripe-hosted Checkout URL.
//
// Price ids live on the billing Worker as Wrangler secrets
// (STRIPE_PRICE_MONTHLY / STRIPE_PRICE_ANNUAL); the SPA passes the plan
// label only. That means rotating prices is a `wrangler secret put` call
// instead of a frontend redeploy.

export function UpgradeRoute() {
	const [params] = useSearchParams();
	const [banner, setBanner] = useState<string | null>(null);

	useEffect(() => {
		// Stripe's hosted Checkout redirects back here with ?canceled=true on
		// cancel. We don't show a success banner on this route — the success
		// redirect goes to /app?upgrade=success (handled by the dashboard).
		if (params.get("canceled") === "true") {
			setBanner("Checkout canceled. You can try again any time.");
		}
	}, [params]);

	return (
		<div className="mx-auto max-w-4xl space-y-8 px-4 py-8">
			<header>
				<Link
					to="/"
					className="text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]"
				>
					← Dashboard
				</Link>
				<h1 className="mt-1 text-2xl font-bold">Upgrade to Hedge Fam Pro</h1>
				<p className="mt-1 text-sm text-[var(--color-muted)]">
					Premium League Pass, unlimited Fams, and the full Power Ranking.
				</p>
			</header>

			{banner && (
				<div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elev)] px-4 py-3 text-sm">
					{banner}
				</div>
			)}

			<section className="space-y-3">
				<h2 className="text-lg font-semibold">
					Backtest every proposal against the last 5 years.
				</h2>
				<p className="text-sm text-[var(--color-muted)]">
					Example: NVDA long at 5% allocation, 2021 → 2026.
				</p>
				<BacktestCard
					symbol="NVDA"
					direction="long"
					allocationPct={5}
					staticPayload={HERO_DEMO_BACKTEST}
				/>
			</section>

			<section className="grid gap-4 sm:grid-cols-2">
				<PlanCard
					title="Monthly"
					priceLabel="$4.99"
					period="/month"
					savingsLabel={null}
					plan="monthly"
				/>
				<PlanCard
					title="Annual"
					priceLabel="$49.99"
					period="/year"
					savingsLabel="Save ~$10 vs monthly"
					plan="annual"
					emphasize
				/>
			</section>

			<p className="text-center text-xs text-[var(--color-muted)]">
				Cancel anytime. Manage your plan from{" "}
				<Link to="/settings/billing" className="underline">
					Settings → Billing
				</Link>
				.
			</p>
		</div>
	);
}

interface PlanCardProps {
	title: string;
	priceLabel: string;
	period: string;
	savingsLabel: string | null;
	plan: BillingCycle;
	emphasize?: boolean;
}

function PlanCard({
	title,
	priceLabel,
	period,
	savingsLabel,
	plan,
	emphasize,
}: PlanCardProps) {
	const start = useStartCheckout(plan);

	const cardClass = [
		"rounded-lg border bg-[var(--color-bg-card)] p-6 transition",
		emphasize
			? "border-[var(--color-accent)] shadow-[0_0_0_1px_var(--color-accent)]"
			: "border-[var(--color-border)] hover:border-[var(--color-accent)]",
	].join(" ");

	return (
		<div className={cardClass}>
			<div className="flex items-start justify-between">
				<div>
					<h2 className="text-base font-semibold">{title}</h2>
					{savingsLabel && (
						<p className="mt-0.5 text-xs text-[var(--color-accent)]">
							{savingsLabel}
						</p>
					)}
				</div>
			</div>
			<div className="mt-4 flex items-baseline gap-1">
				<span className="text-3xl font-bold">{priceLabel}</span>
				<span className="text-sm text-[var(--color-muted)]">{period}</span>
			</div>
			<ul className="mt-4 space-y-2 text-sm text-[var(--color-muted)]">
				<li>Premium League Pass (all weekly themes)</li>
				<li>Unlimited Fams</li>
				<li>Full Power Ranking</li>
			</ul>
			<div className="mt-6">
				<button
					type="button"
					disabled={start.isPending}
					onClick={() => start.mutate()}
					className="w-full rounded-md bg-[var(--color-accent)] px-3 py-2 text-sm font-semibold text-black transition hover:bg-emerald-400 disabled:opacity-60"
				>
					{start.isPending ? "Redirecting…" : "Subscribe"}
				</button>
				{start.isError && (
					<p className="mt-2 text-xs text-[var(--color-danger)]">
						{start.error.message}
					</p>
				)}
			</div>
		</div>
	);
}
